import React, { useEffect, useState, useMemo } from "react";
import CuModal from "../../common_component/component/CuModal";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";

// Helper: BFS-based conversion rate calculator (case-insensitive).
const getConversionRate = (from, to, conversions) => {
  // Normalize both units to uppercase.
  const fromUpper = from.toUpperCase();
  const toUpper = to.toUpperCase();
  console.log("Check getConversionRate", fromUpper, toUpper);
  if (fromUpper === toUpper) return 1;

  const graph = {};
  conversions?.forEach((conv) => {
    const key = conv.unit_name.toUpperCase();
    const base = conv.base_unit.toUpperCase();
    if (!graph[key]) {
      graph[key] = [];
    }
    graph[key].push({
      unit: base,
      rate: parseFloat(conv.conversion_rate),
    });
  });

  const queue = [{ unit: fromUpper, product: 1 }];
  const visited = new Set();
  while (queue.length > 0) {
    const { unit, product } = queue.shift();
    if (unit === toUpper) return product;
    visited.add(unit);
    const neighbors = graph[unit] || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor.unit)) {
        queue.push({ unit: neighbor.unit, product: product * neighbor.rate });
      }
    }
  }
  return 1;
};

const AddItemModal = ({
  onClose,
  onAddItem,
  rackPath,
  item = null,
  pharmacy_id,
}) => {
  // Main form state (including computed keys).
  const [formData, setFormData] = useState({
    batchNumber: "",
    inwardQuantity: 0,
    freeQuantity: 0, // NEW: Free quantity provided by the supplier
    pricePerUnit: 0, // Wholesale rate per UoP unit
    price: 0, // MRP per UoP unit
    unitOfMeasure: "Units", // This will be set to the sale unit (e.g., "TABLET")
    uop: null, // UoM object (e.g., { id, unit_name:"BOX", base_unit:"STRIP", conversion_rate:"20.0000", ... })
    profitMargin: 0,
    profitPerUnit: 0,
    taxableValue: 0,
    cgstRate: 0,
    sgstRate: 0,
    igstRate: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGST: 0,
    totalAmount: 0,
    receivedDate: "",
    expiryDate: "",
    hsn: "",
    item: null,
    location: null,
    effectiveQuantity: 0,
    effectivePricePerBase: 0,
    effectiveMRPPerBase: 0,
  });

  // Local state for controlled selects.
  const [selectedUoPValue, setSelectedUoPValue] = useState(null);
  const [selectedSaleUnitValue, setSelectedSaleUnitValue] = useState(null);
  const [isMedicine, setIsMedicine] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [combinedOptions, setCombinedOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  // Fetch items data.
  const { data: itemsData, isLoading: isLoadingItem } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getItem&items_name=${searchInput}&give_location=True`,
    !!searchInput
  );
  const { data: medicineData, isLoading: isLoadingMedicine } = useItemsFilter(
    ApiEndPoint.SEARCH_MEDICINE_DATA,
    `?medicine_name=${searchInput}`,
    isMedicine && itemsData?.length === 0
  );

  // Fetch UoM conversion data.
  const { data: UomData, isLoading: isLoadingUom } = useItemsFilter(
    ApiEndPoint.INVENTORY,
    `?action=getUnitConversion&pharmacy_id=${pharmacy_id}`
  );

  // Prepare UoP options from UomData.
  const uopOptions =
    UomData?.map((u) => ({
      value: String(u.id),
      label: `${u.unit_name} - ${u.base_unit} (x${Number(u.conversion_rate)})`,
      data: u,
    })) || [];

  const saleUnitOptions = Array.isArray(UomData)
    ? Array.from(
        new Set(
          UomData.flatMap((u) => (u.base_unit === "" ? [u.unit_name] : []))
        )
      ).map((unit) => ({
        value: unit,
        label: unit,
      }))
    : [];

  // Update location options when item changes.
  useEffect(() => {
    if (formData?.item && formData.item.item_id) {
      const selectedItem = combinedOptions?.find(
        (opt) => opt.value === formData.item.item_id
      );
      if (selectedItem && selectedItem.location) {
        if (Array.isArray(selectedItem.location)) {
          setLocationOptions(
            selectedItem?.location?.map((loc) => ({
              value: loc.box_id,
              label: loc.name,
            }))
          );
        } else if (selectedItem?.location?.box_id) {
          setLocationOptions([
            {
              value: selectedItem.location.box_id,
              label: selectedItem.location.name,
            },
          ]);
        }
      }
    }
  }, [formData.item, combinedOptions]);

  // Update combinedOptions when itemsData or medicineData changes.
  useEffect(() => {
    if (itemsData?.length > 0) {
      const itemOptions = itemsData?.map((item) => ({
        value: String(item.id),
        label: item.items_name,
        location: item.location,
      }));
      setCombinedOptions(itemOptions);
    } else if (itemsData?.length === 0 && medicineData && isMedicine) {
      const medicineOptions = medicineData?.map((item) => ({
        value: String(item.id),
        label: item.medicine_name,
        generic_name: medicineData?.generic_name,
        __isNew__: true,
      }));
      setCombinedOptions([...medicineOptions]);
    }
  }, [itemsData, medicineData, isMedicine]);

  // Synchronize local selected UoP value.
  useEffect(() => {
    if (formData.uop) {
      setSelectedUoPValue(String(formData.uop.id));
    } else {
      setSelectedUoPValue(null);
    }
  }, [formData.uop]);

  // Synchronize local selected sale unit value.
  useEffect(() => {
    if (formData.unitOfMeasure) {
      setSelectedSaleUnitValue(formData.unitOfMeasure);
    } else {
      setSelectedSaleUnitValue(null);
    }
  }, [formData.unitOfMeasure]);

  // Update form state and recalculate taxable, GST, and total amount.
  const handleInputChange = (field, value) => {
    const updated = { ...formData, [field]: value };

    if (
      [
        "pricePerUnit",
        "inwardQuantity",
        "price",
        "cgstRate",
        "sgstRate",
        "igstRate",
      ].includes(field)
    ) {
      const pricePerUnit = parseFloat(updated.pricePerUnit) || 0;
      const inwardQuantity = parseFloat(updated.inwardQuantity) || 0;
      const price = parseFloat(updated.price) || 0;
      const taxableValue = pricePerUnit * inwardQuantity;
      updated.taxableValue = taxableValue;
      const cgst = (taxableValue * (parseFloat(updated.cgstRate) || 0)) / 100;
      const sgst = (taxableValue * (parseFloat(updated.sgstRate) || 0)) / 100;
      const igst = (taxableValue * (parseFloat(updated.igstRate) || 0)) / 100;
      updated.cgst = cgst;
      updated.sgst = sgst;
      updated.igst = igst;
      updated.totalGST = cgst + sgst + igst;
      // Calculate totalAmount as taxableValue plus totalGST.
      updated.totalAmount = taxableValue + updated.totalGST;
      if (price > 0 && pricePerUnit > 0) {
        const profitPerUnit = price - pricePerUnit;
        const profitMargin = (profitPerUnit / pricePerUnit) * 100;
        updated.profitPerUnit = profitPerUnit.toFixed(2);
        updated.profitMargin = profitMargin.toFixed(2);
      } else {
        updated.profitPerUnit = 0;
        updated.profitMargin = 0;
      }
    }
    setFormData(updated);
  };

  const handleSubmit = () => {
    if (!formData.item) {
      toast.error("Please select or create an item.");
      return;
    }
    if (!formData.hsn) {
      toast.error("HSN Code is required.");
      return;
    }
    if (!formData.batchNumber.trim()) {
      toast.error("Batch Number is required.");
      return;
    }
    if (!formData.inwardQuantity || parseFloat(formData.inwardQuantity) <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    if (!formData.pricePerUnit || parseFloat(formData.pricePerUnit) <= 0) {
      toast.error("Batch Rate is required and must be greater than zero.");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("MRP is required and must be greater than zero.");
      return;
    }
    if (parseFloat(formData.price) < parseFloat(formData.pricePerUnit)) {
      toast.error("MRP cannot be less than the Batch Rate.");
      return;
    }
    if (!formData.receivedDate) {
      toast.error("Received Date is required.");
      return;
    }
    if (!formData.expiryDate) {
      toast.error("Expiry Date is required.");
      return;
    }
    if (new Date(formData.receivedDate) >= new Date(formData.expiryDate)) {
      toast.error("Expiry Date must be after Received Date.");
      return;
    }
    onAddItem(formData);
  };

  // If editing an existing item, populate form.
  useEffect(() => {
    if (item) {
      setFormData(item);
    }
  }, [item]);

  // --- Effective Values Calculation Using Conversion Factor ---
  // We use formData.uop.unit_name (e.g., "BOX") and formData.unitOfMeasure (e.g., "TABLET").
  const conversionFactor = useMemo(() => {
    if (formData.uop && formData.unitOfMeasure && UomData) {
      return getConversionRate(
        formData.uop.unit_name,
        formData.unitOfMeasure,
        UomData
      );
    }
    return 1;
  }, [formData.uop, formData.unitOfMeasure, UomData]);

  // Calculate purchased and free quantities.
  const purchasedQty = parseFloat(formData.inwardQuantity) || 0;
  const freeQty = parseFloat(formData.freeQuantity) || 0;

  // Effective total sale units (e.g., total TABLETs) now include free units.
  const computedEffectiveQuantity = (purchasedQty + freeQty) * conversionFactor;

  // Compute total batch price and total MRP (based on purchased quantity only).
  const totalBatchPrice =
    (parseFloat(formData.pricePerUnit) || 0) * purchasedQty;
  const totalMRP = (parseFloat(formData.price) || 0) * purchasedQty;

  // Effective prices per sale unit (e.g., per TABLET)
  const computedEffectivePricePerBase =
    computedEffectiveQuantity > 0
      ? totalBatchPrice / computedEffectiveQuantity
      : 0;
  const computedEffectiveMRPPerBase =
    computedEffectiveQuantity > 0 ? totalMRP / computedEffectiveQuantity : 0;

  // Store the computed values in formData.
  useEffect(() => {
    // Only update if there's a change to prevent infinite loops.
    if (
      formData.effectiveQuantity !== computedEffectiveQuantity ||
      formData.effectivePricePerBase !== computedEffectivePricePerBase ||
      formData.effectiveMRPPerBase !== computedEffectiveMRPPerBase
    ) {
      setFormData((prev) => ({
        ...prev,
        effectiveQuantity: computedEffectiveQuantity,
        effectivePricePerBase: computedEffectivePricePerBase,
        effectiveMRPPerBase: computedEffectiveMRPPerBase,
      }));
    }
  }, [
    computedEffectiveQuantity,
    computedEffectivePricePerBase,
    computedEffectiveMRPPerBase,
    formData.effectiveQuantity,
    formData.effectivePricePerBase,
    formData.effectiveMRPPerBase,
  ]);

  return (
    <CuModal isOpen onClose={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-gray-800 mb-6">
          {item ? "Edit Item" : "Add Item"}
        </h3>

        {/* Section: Item Selection */}
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="selectMedicine"
              className="w-4 h-4 border-gray-300 rounded focus:ring-indigo-500"
              checked={isMedicine}
              onChange={(e) => setIsMedicine(e.target.checked)}
            />
            <label
              htmlFor="selectMedicine"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Select Medicine
            </label>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isMedicine ? "Item Name/Brand Name*" : "Item Name*"}
          </label>
          <CreatableSelect
            isClearable
            isLoading={isLoadingItem || isLoadingMedicine}
            loadingMessage={() => "Loading..."}
            options={combinedOptions}
            value={
              formData.item
                ? {
                    label: formData.item.item_name,
                    value: formData.item.item_id,
                  }
                : null
            }
            onChange={(selectedOption) => {
              if (selectedOption) {
                if (selectedOption.location) {
                  if (Array.isArray(selectedOption.location)) {
                    setLocationOptions(
                      selectedOption?.location?.map((loc) => ({
                        value: loc.box_id,
                        label: loc.name,
                      }))
                    );
                  } else {
                    setLocationOptions([
                      {
                        value: selectedOption.location.box_id,
                        label: selectedOption.location.name,
                      },
                    ]);
                  }
                }
                handleInputChange("item", {
                  item_name: selectedOption.label,
                  item_id: selectedOption.__isNew__
                    ? null
                    : selectedOption.value,
                });
              } else {
                handleInputChange("item", null);
              }
            }}
            onInputChange={(inputValue) => setSearchInput(inputValue)}
            placeholder="Select or create an item"
            className="mb-2"
            styles={{
              control: (provided) => ({
                ...provided,
                borderColor: "gray",
                boxShadow: "none",
              }),
            }}
          />
        </div>

        {/* Section: Unit & Location */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit of Purchase (UoP)*
            </label>
            <select
              value={selectedUoPValue || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  handleInputChange("uop", null);
                  setSelectedUoPValue(null);
                } else {
                  setSelectedUoPValue(value);
                  const selectedOption = uopOptions.find(
                    (opt) => opt.value === value
                  );
                  if (selectedOption) {
                    handleInputChange("uop", selectedOption.data);
                  }
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select Unit of Purchase</option>
              {uopOptions?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <Select
              isClearable
              options={locationOptions}
              value={
                formData.location
                  ? {
                      label: formData.location.name,
                      value: formData.location.id,
                    }
                  : null
              }
              onChange={(selectedOption) => {
                if (!selectedOption) {
                  handleInputChange("location", null);
                } else {
                  handleInputChange("location", {
                    name: selectedOption.label,
                    id: selectedOption.value,
                  });
                }
              }}
              placeholder="Select a location"
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.value}
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderColor: "gray",
                  boxShadow: "none",
                }),
              }}
            />
          </div>
        </div>

        {/* Section: Unit of Measure for Sale */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit of Measure (for sale)
          </label>
          <select
            value={selectedSaleUnitValue || ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedSaleUnitValue(value);
              handleInputChange("unitOfMeasure", value);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select Unit of Measure for Sale</option>
            {saleUnitOptions?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Section: Batch & Product Details */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Number*
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.batchNumber || ""}
              onChange={(e) => handleInputChange("batchNumber", e.target.value)}
              placeholder="Enter batch number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HSN*
            </label>
            <input
              type="number"
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.hsn || ""}
              onChange={(e) => handleInputChange("hsn", e.target.value)}
              placeholder="Enter HSN code"
            />
          </div>
        </div>

        {/* Section: Quantity & Pricing */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column: Purchased & Free Quantity */}
          <div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity*
              </label>
              <input
                type="number"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.inwardQuantity || ""}
                onChange={(e) =>
                  handleInputChange("inwardQuantity", e.target.value)
                }
                placeholder="Enter quantity"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Free Quantity
              </label>
              <input
                type="number"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.freeQuantity || ""}
                onChange={(e) =>
                  handleInputChange("freeQuantity", e.target.value)
                }
                placeholder="Enter free quantity"
              />
            </div>
          </div>
          {/* Right Column: Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch Rate (₹)*
              </label>
              <input
                type="number"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.pricePerUnit || ""}
                onChange={(e) =>
                  handleInputChange("pricePerUnit", e.target.value)
                }
                placeholder="Enter rate per UoP"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MRP (₹)*
              </label>
              <input
                type="number"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData.price || ""}
                onChange={(e) => handleInputChange("price", e.target.value)}
                placeholder="Enter MRP per UoP"
              />
            </div>
          </div>
        </div>

        {/* Section: GST Rates */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-3">
            GST Rates
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "CGST Rate (%)", field: "cgstRate", type: "number" },
              { label: "SGST Rate (%)", field: "sgstRate", type: "number" },
              { label: "IGST Rate (%)", field: "igstRate", type: "number" },
            ]?.map((input) => (
              <div key={input.field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {input.label}
                </label>
                <input
                  type={input.type}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={formData[input.field] || ""}
                  onChange={(e) =>
                    handleInputChange(
                      input.field,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  placeholder={`Enter ${input.label}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Section: Dates */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: "Received Date*", field: "receivedDate", type: "date" },
            { label: "Expiry Date*", field: "expiryDate", type: "date" },
          ]?.map((input) => (
            <div key={input.field}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {input.label}
              </label>
              <input
                type={input.type}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={formData[input.field] || ""}
                onChange={(e) => handleInputChange(input.field, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Section: Summary */}
        <div className="mb-6 bg-white border border-gray-200 rounded p-4">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">Summary</h4>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-gray-600">Taxable Value:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.taxableValue)?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">CGST:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.cgst)?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">SGST:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.sgst)?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">IGST:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.igst)?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Total GST:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.totalGST)?.toFixed(2) || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Profit per Unit:</p>
              <p className="font-semibold">
                ₹{formData.profitPerUnit || "0.00"}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Profit Margin:</p>
              <p className="font-semibold">
                {formData.profitMargin || "0.00"}%
              </p>
            </div>
            <div>
              <p className="text-gray-600">Total Amount:</p>
              <p className="font-semibold">
                ₹{parseFloat(formData.totalAmount)?.toFixed(2) || "0.00"}
              </p>
            </div>
            {formData.uop && formData.unitOfMeasure && (
              <>
                <div>
                  <p className="text-gray-600">
                    Total {formData.unitOfMeasure}s:
                  </p>
                  <p className="font-semibold">
                    {isNaN(formData.effectiveQuantity)
                      ? "0.00"
                      : formData.effectiveQuantity.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">
                    Price per {formData.unitOfMeasure}:
                  </p>
                  <p className="font-semibold">
                    ₹
                    {isNaN(formData.effectivePricePerBase)
                      ? "0.00"
                      : formData.effectivePricePerBase.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">
                    MRP per {formData.unitOfMeasure}:
                  </p>
                  <p className="font-semibold">
                    ₹
                    {isNaN(formData.effectiveMRPPerBase)
                      ? "0.00"
                      : formData.effectiveMRPPerBase.toFixed(2)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Section: Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition duration-150"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 transition duration-150"
            onClick={handleSubmit}
          >
            {item ? "Update Item" : "Add Item"}
          </button>
        </div>
      </div>
    </CuModal>
  );
};

export default AddItemModal;
